// frontend/src/services/notificationService.js
import { notifications } from '@mantine/notifications';
import i18n from '../i18n';
import {
  IconCheck,
  IconX,
  IconAlertTriangle,
  IconInfoCircle,
  IconBell,
  IconSparkles,
  IconHeadset,
  IconMessage,
  IconUserCheck,
  IconUserOff,
  IconWifi,
  IconWifiOff,
  IconRocket
} from '@tabler/icons-react';

// Configurazione stili base per le notifiche Zeus
const baseStyles = {
  root: {
    borderRadius: '12px',
    padding: '16px',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 12px 30px rgba(0, 0, 0, 0.15)'
    }
  },
  icon: {
    borderRadius: '8px',
    width: '36px',
    height: '36px'
  },
  title: {
    fontWeight: 700,
    fontSize: '15px'
  },
  description: {
    fontSize: '14px',
    opacity: 0.9
  },
  closeButton: {
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
      transform: 'scale(1.1)'
    }
  }
};

// Configurazioni per tipo di notifica
const notificationConfigs = {
  success: {
    color: 'green',
    icon: <IconCheck size={20} />,
    gradient: 'linear-gradient(135deg, rgba(37, 211, 102, 0.05), rgba(37, 211, 102, 0.1))',
    borderColor: 'rgba(37, 211, 102, 0.2)',
    iconBg: '#25d366'
  },
  error: {
    color: 'red',
    icon: <IconX size={20} />,
    gradient: 'linear-gradient(135deg, rgba(255, 71, 87, 0.05), rgba(255, 71, 87, 0.1))',
    borderColor: 'rgba(255, 71, 87, 0.2)',
    iconBg: '#ff4757'
  },
  warning: {
    color: 'yellow',
    icon: <IconAlertTriangle size={20} />,
    gradient: 'linear-gradient(135deg, rgba(255, 193, 7, 0.05), rgba(255, 193, 7, 0.1))',
    borderColor: 'rgba(255, 193, 7, 0.2)',
    iconBg: '#ffc107'
  },
  info: {
    color: 'blue',
    icon: <IconInfoCircle size={20} />,
    gradient: 'linear-gradient(135deg, rgba(47, 50, 137, 0.05), rgba(64, 38, 106, 0.05))',
    borderColor: 'rgba(47, 50, 137, 0.2)',
    iconBg: '#2F3289'
  },
  message: {
    color: 'blue',
    icon: <IconMessage size={20} />,
    gradient: 'linear-gradient(135deg, rgba(47, 50, 137, 0.05), rgba(64, 38, 106, 0.05))',
    borderColor: 'rgba(47, 50, 137, 0.2)',
    iconBg: '#2F3289'
  },
  handover: {
    color: 'purple',
    icon: <IconHeadset size={20} />,
    gradient: 'linear-gradient(135deg, rgba(64, 38, 106, 0.05), rgba(47, 50, 137, 0.05))',
    borderColor: 'rgba(64, 38, 106, 0.2)',
    iconBg: '#40266A'
  },
  connection: {
    color: 'teal',
    icon: <IconWifi size={20} />,
    gradient: 'linear-gradient(135deg, rgba(20, 184, 166, 0.05), rgba(20, 184, 166, 0.1))',
    borderColor: 'rgba(20, 184, 166, 0.2)',
    iconBg: '#14b8a6'
  },
  rocket: {
    color: 'indigo',
    icon: <IconRocket size={20} />,
    gradient: 'linear-gradient(135deg, rgba(79, 70, 229, 0.05), rgba(124, 58, 237, 0.05))',
    borderColor: 'rgba(79, 70, 229, 0.2)',
    iconBg: '#4f46e5'
  }
};

// Funzione helper per creare stili personalizzati
const createNotificationStyles = (type) => {
  const config = notificationConfigs[type] || notificationConfigs.info;

  return {
    ...baseStyles,
    root: {
      ...baseStyles.root,
      background: config.gradient,
      borderLeft: `4px solid ${config.iconBg}`,
      borderTop: `1px solid ${config.borderColor}`,
      borderRight: `1px solid ${config.borderColor}`,
      borderBottom: `1px solid ${config.borderColor}`
    }
  };
};

// Funzioni pubbliche per mostrare notifiche

/**
 * Mostra una notifica di successo
 */
export const showSuccessNotification = (title, message = '', options = {}) => {
  notifications.show({
    title,
    message,
    color: 'green',
    icon: notificationConfigs.success.icon,
    autoClose: 5000,
    withCloseButton: true,
    styles: createNotificationStyles('success'),
    ...options
  });
};

/**
 * Mostra una notifica di errore
 */
export const showErrorNotification = (title, message = '', options = {}) => {
  notifications.show({
    title,
    message,
    color: 'red',
    icon: notificationConfigs.error.icon,
    autoClose: 7000,
    withCloseButton: true,
    styles: createNotificationStyles('error'),
    ...options
  });
};

/**
 * Mostra una notifica di avviso
 */
export const showWarningNotification = (title, message = '', options = {}) => {
  notifications.show({
    title,
    message,
    color: 'yellow',
    icon: notificationConfigs.warning.icon,
    autoClose: 6000,
    withCloseButton: true,
    styles: createNotificationStyles('warning'),
    ...options
  });
};

/**
 * Mostra una notifica informativa
 */
export const showInfoNotification = (title, message = '', options = {}) => {
  notifications.show({
    title,
    message,
    color: 'blue',
    icon: notificationConfigs.info.icon,
    autoClose: 5000,
    withCloseButton: true,
    styles: createNotificationStyles('info'),
    ...options
  });
};

/**
 * Notifiche specifiche per handover
 */
export const showHandoverNotification = {
  newRequest: (channelName = 'chat') => {
    notifications.show({
      title: i18n.t('notifications:handover.newRequestTitle'),
      message: i18n.t('notifications:handover.newRequestMessage', { channel: channelName }),
      color: 'blue',
      icon: <IconBell size={20} />,
      autoClose: 5000,
      withCloseButton: true,
      styles: createNotificationStyles('handover'),
      position: 'top-right'
    });
  },

  assigned: (channelName = 'chat') => {
    showSuccessNotification(
      i18n.t('notifications:handover.assignedTitle'),
      i18n.t('notifications:handover.assignedMessage', { channel: channelName }),
      {
        icon: <IconUserCheck size={20} />,
        position: 'top-center'
      }
    );
  },

  terminated: (channelName = 'chat') => {
    showSuccessNotification(
      i18n.t('notifications:handover.terminatedTitle'),
      i18n.t('notifications:handover.terminatedMessage', { channel: channelName }),
      {
        icon: <IconUserOff size={20} />,
        position: 'top-center'
      }
    );
  },

  message: (type = 'client', channelName = 'chat') => {
    const isClient = type === 'client';
    notifications.show({
      title: i18n.t('notifications:handover.newMessageTitle'),
      message: isClient
        ? i18n.t('notifications:handover.newMessageClient', { channel: channelName })
        : i18n.t('notifications:handover.newMessageOperator'),
      color: 'blue',
      icon: <IconMessage size={20} />,
      autoClose: 4000,
      withCloseButton: true,
      styles: createNotificationStyles('message'),
      position: 'bottom-right'
    });
  },

  notAssignedWarning: () => {
    showWarningNotification(
      i18n.t('notifications:handover.notAssignedTitle'),
      i18n.t('notifications:handover.notAssignedMessage')
    );
  }
};

/**
 * Notifiche per connessione SSE
 */
export const showConnectionNotification = {
  connected: () => {
    notifications.show({
      title: i18n.t('notifications:connection.connectedTitle'),
      message: i18n.t('notifications:connection.connectedMessage'),
      color: 'teal',
      icon: <IconWifi size={20} />,
      autoClose: 3000,
      withCloseButton: false,
      styles: createNotificationStyles('connection'),
      position: 'bottom-center'
    });
  },

  disconnected: () => {
    notifications.show({
      title: i18n.t('notifications:connection.disconnectedTitle'),
      message: i18n.t('notifications:connection.disconnectedMessage'),
      color: 'red',
      icon: <IconWifiOff size={20} />,
      autoClose: false,
      withCloseButton: true,
      styles: createNotificationStyles('error'),
      position: 'bottom-center',
      id: 'connection-lost'
    });
  },

  reconnected: () => {
    // Rimuovi la notifica di disconnessione se presente
    notifications.hide('connection-lost');

    showConnectionNotification.connected();
  }
};

/**
 * Notifica con progresso (per upload, operazioni lunghe, ecc.)
 */
export const showProgressNotification = (id, title, progress = 0) => {
  notifications.show({
    id,
    title,
    message: (
      <div style={{ marginTop: '8px' }}>
        <div style={{
          width: '100%',
          height: '4px',
          backgroundColor: 'rgba(47, 50, 137, 0.1)',
          borderRadius: '2px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${progress}%`,
            height: '100%',
            background: 'linear-gradient(45deg, #2F3289, #40266A)',
            borderRadius: '2px',
            transition: 'width 0.3s ease'
          }} />
        </div>
        <span style={{ fontSize: '12px', opacity: 0.7, marginTop: '4px' }}>
          {progress}%
        </span>
      </div>
    ),
    color: 'blue',
    icon: <IconRocket size={20} />,
    autoClose: false,
    withCloseButton: false,
    styles: createNotificationStyles('rocket')
  });
};

/**
 * Aggiorna una notifica esistente
 */
export const updateNotification = notifications.update;

/**
 * Rimuovi una notifica
 */
export const hideNotification = notifications.hide;

/**
 * Rimuovi tutte le notifiche
 */
export const cleanNotifications = notifications.clean;

/**
 * Notifica custom con pieno controllo
 */
export const showCustomNotification = (options) => {
  const type = options.type || 'info';
  const styles = options.styles || createNotificationStyles(type);

  notifications.show({
    autoClose: 5000,
    withCloseButton: true,
    position: 'top-right',
    ...options,
    styles: {
      ...styles,
      ...options.styles
    }
  });
};

// Esporta anche le icone comuni per uso esterno
export const NotificationIcons = {
  success: <IconCheck size={20} />,
  error: <IconX size={20} />,
  warning: <IconAlertTriangle size={20} />,
  info: <IconInfoCircle size={20} />,
  message: <IconMessage size={20} />,
  handover: <IconHeadset size={20} />,
  bell: <IconBell size={20} />,
  sparkles: <IconSparkles size={20} />
};

// Funzione di inizializzazione (opzionale)
export const initializeNotifications = () => {
  // Puoi aggiungere configurazioni globali qui se necessario
};